import React, { useState, useEffect } from 'react';
import axios from 'axios';
import PortalAdminLayout from '../admin/PortalAdminLayout';
import './PortalAdminStyles.css';

const ProfessionalTypesManagement = () => {
  const [professionalTypes, setProfessionalTypes] = useState([]);
  const [businessCategories, setBusinessCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [formData, setFormData] = useState({
    value: '',
    label: '',
    icon: '',
    description: '',
    active: true,
    businessCategories: []
  });

  useEffect(() => {
    fetchProfessionalTypes();
    fetchBusinessCategories();
  }, []);

  const fetchProfessionalTypes = async () => {
    try {
      const token = localStorage.getItem('ogestyle_token');
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/platform-settings/professional-types`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProfessionalTypes(res.data.professionalTypes || []);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const fetchBusinessCategories = async () => {
    try {
      const token = localStorage.getItem('ogestyle_token');
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/platform-settings/business-categories/all`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBusinessCategories(res.data.types || []);
    } catch (error) {
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleBusinessTypeToggle = (businessCategory) => {
    setFormData(prev => ({
      ...prev,
      businessCategories: prev.businessCategories.includes(businessCategory)
        ? prev.businessCategories.filter(t => t !== businessCategory)
        : [...prev.businessCategories, businessCategory]
    }));
  };

  const handleAdd = () => {
    setFormData({
      value: '',
      label: '',
      icon: '',
      description: '',
      active: true,
      businessCategories: []
    });
    setEditingType(null);
    setShowAddModal(true);
  };

  const handleEdit = (type) => {
    setFormData({
      value: type.value,
      label: type.label,
      icon: type.icon || '',
      description: type.description || '',
      active: type.active !== false,
      businessCategories: type.businessCategories || []
    });
    setEditingType(type.value);
    setShowAddModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('ogestyle_token');
      
      if (editingType) {
        // Update existing
        await axios.put(
          `${process.env.REACT_APP_API_BASE_URL}/api/platform-settings/professional-types/${editingType}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert('Professional type updated successfully!');
      } else {
        // Create new
        await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/api/platform-settings/professional-types`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert('Professional type added successfully!');
      }
      
      setShowAddModal(false);
      fetchProfessionalTypes();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to save professional type');
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('ogestyle_token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/platform-settings/professional-types/export`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `professional-types-export-${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Failed to export professional types');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      if (!data.professionalTypes || !Array.isArray(data.professionalTypes)) {
        alert('Invalid import file format');
        return;
      }

      const mode = window.confirm(
        `Import ${data.professionalTypes.length} professional types.\n\n` +
        `Click OK to MERGE with existing (recommended)\n` +
        `Click Cancel to REPLACE all existing types`
      ) ? 'merge' : 'replace';

      const token = localStorage.getItem('ogestyle_token');
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/platform-settings/professional-types/import`,
        { professionalTypes: data.professionalTypes, mode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert(`Successfully imported ${data.professionalTypes.length} types (${mode} mode)`);
      fetchProfessionalTypes();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to import professional types');
    }
    
    // Reset file input
    e.target.value = '';
  };

  const handleDelete = async (value) => {
    if (!window.confirm(`Are you sure you want to delete ${value}?`)) return;
    
    try {
      const token = localStorage.getItem('ogestyle_token');
      await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/api/platform-settings/professional-types/${value}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Professional type deleted successfully!');
      fetchProfessionalTypes();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to delete professional type');
    }
  };

  const toggleActive = async (type) => {
    try {
      const token = localStorage.getItem('ogestyle_token');
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/platform-settings/professional-types/${type.value}`,
        { ...type, active: !type.active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchProfessionalTypes();
    } catch (error) {
      alert('Failed to update status');
    }
  };

  if (loading) {
    return (
      <PortalAdminLayout>
        <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>
      </PortalAdminLayout>
    );
  }

  return (
    <PortalAdminLayout>
      <div style={{ padding: '30px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '28px', fontWeight: '700', color: '#001d4a' }}>Professional Types</h1>
          <p style={{ margin: '8px 0 0 0', color: '#6b7280', fontSize: '15px' }}>
            Manage professional roles available across the platform ({professionalTypes.length} total)
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <label
            style={{
              padding: '12px 24px',
              background: '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            📥 Import
            <input 
              type="file" 
              accept=".json" 
              onChange={handleImport} 
              style={{ display: 'none' }}
            />
          </label>
          <button
            onClick={handleExport}
            style={{
              padding: '12px 24px',
              background: '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            📤 Export
          </button>
          <button
            onClick={handleAdd}
            style={{
              padding: '12px 24px',
              background: '#003087',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            ➕ Add Professional Type
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gap: '16px' }}>
        {professionalTypes.map(type => (
          <div
            key={type.value}
            style={{
              background: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              padding: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '20px'
            }}
          >
            <div style={{ fontSize: '32px', flexShrink: 0 }}>{type.icon || '👤'}</div>
            
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '6px' }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#001d4a' }}>
                  {type.label}
                </h3>
                <span
                  style={{
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '600',
                    background: type.active ? '#d1fae5' : '#fee2e2',
                    color: type.active ? '#065f46' : '#991b1b'
                  }}
                >
                  {type.active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <p style={{ margin: '0 0 8px 0', color: '#6b7280', fontSize: '14px' }}>
                {type.description}
              </p>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {type.businessCategories && type.businessCategories.map(bt => (
                  <span
                    key={bt}
                    style={{
                      padding: '4px 10px',
                      background: '#eff6ff',
                      color: '#1e40af',
                      fontSize: '12px',
                      borderRadius: '6px',
                      fontWeight: '500'
                    }}
                  >
                    {businessCategories.find(opt => opt.value === bt)?.label || bt}
                  </span>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', flexShrink: 0 }}>
              <button
                onClick={() => toggleActive(type)}
                style={{
                  padding: '8px 16px',
                  background: type.active ? '#fef3c7' : '#d1fae5',
                  color: type.active ? '#92400e' : '#065f46',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {type.active ? 'Deactivate' : 'Activate'}
              </button>
              <button
                onClick={() => handleEdit(type)}
                style={{
                  padding: '8px 16px',
                  background: '#eff6ff',
                  color: '#1e40af',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Edit
              </button>
              <button
                onClick={() => handleDelete(type.value)}
                style={{
                  padding: '8px 16px',
                  background: '#fee2e2',
                  color: '#991b1b',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {professionalTypes.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9ca3af' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
          <p style={{ fontSize: '16px', fontWeight: '500' }}>No professional types yet</p>
          <p style={{ fontSize: '14px' }}>Click "Add Professional Type" to create one</p>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowAddModal(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '16px',
              padding: '30px',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: '0 0 24px 0', fontSize: '24px', fontWeight: '700', color: '#001d4a' }}>
              {editingType ? 'Edit Professional Type' : 'Add Professional Type'}
            </h2>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                  Value (unique identifier) *
                </label>
                <input
                  type="text"
                  name="value"
                  value={formData.value}
                  onChange={handleInputChange}
                  placeholder="e.g., hair-stylist"
                  required
                  disabled={!!editingType}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '15px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                  Label (display name) *
                </label>
                <input
                  type="text"
                  name="label"
                  value={formData.label}
                  onChange={handleInputChange}
                  placeholder="e.g., Hair Stylist"
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '15px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                  Icon (emoji)
                </label>
                <input
                  type="text"
                  name="icon"
                  value={formData.icon}
                  onChange={handleInputChange}
                  placeholder="e.g., 💇"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '15px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Brief description of this role"
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '15px',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '12px', fontWeight: '600', fontSize: '14px' }}>
                  Business Categories
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                  {businessCategories.map(option => (
                    <label
                      key={option.value}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '10px',
                        background: '#f9fafb',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        cursor: 'pointer'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={formData.businessCategories.includes(option.value)}
                        onChange={() => handleBusinessTypeToggle(option.value)}
                        style={{ marginRight: '8px' }}
                      />
                      <span style={{ fontSize: '14px' }}>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    name="active"
                    checked={formData.active}
                    onChange={handleInputChange}
                    style={{ marginRight: '8px', width: '18px', height: '18px' }}
                  />
                  <span style={{ fontWeight: '600', fontSize: '14px' }}>Active</span>
                </label>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  style={{
                    padding: '12px 24px',
                    background: '#f3f4f6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '12px 24px',
                    background: '#003087',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  {editingType ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
    </PortalAdminLayout>
  );
};

export default ProfessionalTypesManagement;
